import type { IndividualPaymentPersonCourseViewModel } from '@/shared/api/generated';
import { useGetApiV1IndividualpaymentPeoplePersonidCourses } from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';

/**
 * Planos de pagamento (contratos financeiros) de um aluno, para o seletor da
 * consolidação.
 *
 * Consome o mesmo endpoint de `useIndividualPaymentContracts`, mas **sem** os
 * filtros de cobrança da pesquisa: ali eles existem para a subtabela não
 * mostrar contratos que não casam com a linha-pai; aqui não há pesquisa ativa,
 * e filtrar por `OurNumber`/`InvoiceNumber` esconderia planos do aluno sem que
 * ele entendesse por quê.
 *
 * A query fica desabilitada enquanto o `personId` não for um número válido — o
 * id vem do header do contrato, então é `null` até a primeira resposta.
 *
 * @param personId - ID da pessoa; `null` desabilita a query.
 * @returns Planos do aluno e o estado da requisição.
 *
 * @example
 * ```tsx
 * const { plans, isLoading } = usePersonPlans(header?.personId ?? null);
 * ```
 */
export function usePersonPlans(personId: number | null) {
  const { data, isLoading, isError } = useGetApiV1IndividualpaymentPeoplePersonidCourses(
    personId ?? 0,
    undefined,
    { query: { enabled: personId !== null } },
  );

  // O Kubb tipa o corpo como array, mas o backend já foi observado envelopando
  // respostas desta API em `{ data: [...] }` — sem normalizar, o `.map` do
  // seletor quebra em runtime.
  const plans: readonly IndividualPaymentPersonCourseViewModel[] =
    unwrapPagedResponse<IndividualPaymentPersonCourseViewModel>(data);

  return {
    plans,
    isLoading: personId !== null && isLoading,
    isError,
  } as const;
}
